const DEFAULT_SERVER_ERROR_MESSAGE = 'An unexpected server error occurred. Please try again later.';
const NETWORK_ERROR_MESSAGE =
  'A network error occurred. Please check your connection and try again.';
const SERVER_ERROR_STATUS_THRESHOLD = 500;

function resolveStatusCode(apolloLikeError: {
  status?: number;
  networkError?: unknown;
}): number | null {
  if (typeof apolloLikeError.status === 'number') {
    return apolloLikeError.status;
  }
  return getStatusCode(apolloLikeError.networkError);
}

export function getServerErrorMessage(
  error: unknown,
  fallbackMessage = DEFAULT_SERVER_ERROR_MESSAGE
): string {
  if (!error || typeof error !== 'object') {
    return fallbackMessage;
  }

  const apolloLikeError = error as {
    networkError?: unknown;
    graphQLErrors?: Array<{ extensions?: { code?: string } }>;
    message?: string;
    status?: number;
  };

  const statusCode = resolveStatusCode(apolloLikeError);

  if (statusCode && statusCode >= SERVER_ERROR_STATUS_THRESHOLD) {
    return fallbackMessage;
  }

  if (apolloLikeError.networkError) {
    return NETWORK_ERROR_MESSAGE;
  }

  const hasServerGraphQlCode = apolloLikeError.graphQLErrors?.some((graphQLError) => {
    const code = graphQLError?.extensions?.code;
    return (
      code === 'INTERNAL_SERVER_ERROR' ||
      code === 'GRAPHQL_VALIDATION_FAILED' ||
      code === 'UNAUTHENTICATED' ||
      code === 'FORBIDDEN'
    );
  });

  if (hasServerGraphQlCode) {
    return fallbackMessage;
  }

  return fallbackMessage;
}

function getStatusCode(networkError: unknown): number | null {
  if (!networkError || typeof networkError !== 'object') {
    return null;
  }

  const candidate = networkError as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
  };

  if (typeof candidate.status === 'number') {
    return candidate.status;
  }

  if (typeof candidate.statusCode === 'number') {
    return candidate.statusCode;
  }

  if (typeof candidate.response?.status === 'number') {
    return candidate.response.status;
  }

  return null;
}
